const safeLocale = (language: string | undefined): string => {
    try {
        return language && Intl.NumberFormat.supportedLocalesOf(language).length ? language : 'en';
    } catch {
        return 'en';
    }
};

const dateTimeFormat = (locale: string, options: Intl.DateTimeFormatOptions, timeZone: string | undefined) => {
    try {
        return new Intl.DateTimeFormat(locale, {...options, timeZone});
    } catch {
        return new Intl.DateTimeFormat(locale, options);
    }
};

const toDate = (iso: string | undefined): Date | undefined => {
    if (!iso) return undefined;

    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const formatMoney = (
    cents: number,
    currency: string | undefined,
    language: string,
    trimWhole = false,
    currencyDisplay: 'symbol' | 'code' = 'symbol'
): string => {
    const locale = safeLocale(language);
    const fractionDigits = trimWhole && cents % 100 === 0 ? 0 : 2;
    const digits = {minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits};

    if (currency) {
        try {
            return new Intl.NumberFormat(locale, {style: 'currency', currency, currencyDisplay, ...digits}).format(cents / 100);
        } catch {
        }
    }

    const plain = new Intl.NumberFormat(locale, digits).format(cents / 100);
    return currency ? `${plain} ${currency}` : plain;
};

export const formatPercent = (percent: number, language: string, maximumFractionDigits = 1): string => {
    try {
        return new Intl.NumberFormat(safeLocale(language), {style: 'percent', maximumFractionDigits}).format(percent / 100);
    } catch {
        return `${Number(percent.toFixed(maximumFractionDigits))} %`;
    }
};

export const formatDuration = (minutes: number, language: string): string => {
    try {
        return new Intl.NumberFormat(safeLocale(language), {style: 'unit', unit: 'minute', unitDisplay: 'short'}).format(minutes);
    } catch {
        return `${minutes} min`;
    }
};

const DAY: Intl.DateTimeFormatOptions = {day: '2-digit', month: '2-digit', year: 'numeric'};
const TIME: Intl.DateTimeFormatOptions = {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'};

const dateLocale = (language: string) => {
    const locale = safeLocale(language);
    return locale === 'en' ? 'en-GB' : locale;
};

export const formatDay = (iso: string | undefined, language: string, timeZone: string | undefined): string | undefined => {
    const date = toDate(iso);
    return date ? dateTimeFormat(dateLocale(language), DAY, timeZone).format(date) : undefined;
};

export const formatTime = (iso: string | undefined, language: string, timeZone: string | undefined): string | undefined => {
    const date = toDate(iso);
    return date ? dateTimeFormat(dateLocale(language), TIME, timeZone).format(date) : undefined;
};

export const currencySymbol = (currency: string | undefined, language: string): string => {
    if (!currency) return '';

    try {
        return new Intl.NumberFormat(safeLocale(language), {style: 'currency', currency})
            .formatToParts(0)
            .find(part => part.type === 'currency')?.value ?? currency;
    } catch {
        return currency;
    }
};

export interface SigningFormat {
    money: (cents: number) => string;
    moneyShort: (cents: number) => string;
    amount: (cents: number) => string;
    percent: (percent: number, maximumFractionDigits?: number) => string;
    day: (iso?: string) => string | undefined;
    time: (iso?: string) => string | undefined;
    duration: (minutes: number) => string;
    currencySymbol: string;
    amountPlaceholder: string;
}

export const signingFormat = (language: string, currency?: string, timeZone?: string): SigningFormat => ({
    money: cents => formatMoney(cents, currency, language),
    moneyShort: cents => formatMoney(cents, currency, language, true),
    amount: cents => formatMoney(cents, undefined, language),
    percent: (percent, maximumFractionDigits) => formatPercent(percent, language, maximumFractionDigits),
    day: iso => formatDay(iso, language, timeZone),
    time: iso => formatTime(iso, language, timeZone),
    duration: minutes => formatDuration(minutes, language),
    currencySymbol: currencySymbol(currency, language),
    amountPlaceholder: formatMoney(0, undefined, language),
});
